import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Button from "veneer/Button";
import { JollyRancherChip, type JollyRancherChipColor } from "veneer/Chip";
import DataTable, { createColumnHelper } from "veneer/DataTable";
import DescriptionList, { DescriptionListItem } from "veneer/DescriptionList";
import Drawer from "veneer/Drawer";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import Tabs, { Tab, TabRow, TabsProvider } from "veneer/Tabs";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import {
  RESOLVE_INCIDENT,
  UPDATE_INCIDENT_STATUS,
} from "cortex/ui/src/fleet/redux-store/ui-state/incidents/actions";
import { alertV3Service } from "cortex/ui/src/fleet/service/alertV3";
import { eventService } from "cortex/ui/src/fleet/service/event";
import incidentsService, {
  type IncidentComment,
} from "cortex/ui/src/fleet/service/incidents";
import { Alert as AlertModel } from "cortex/ui/src/fleet/view/alert/model";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { Event } from "cortex/ui/src/fleet/view/event/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { compatApplicationsSelector } from "cortex/ui/src/redux-store/applications/selectors";
import { devicesSelector } from "cortex/ui/src/redux-store/devices/selectors";
import {
  formatApplicationSource,
  formatDateTimeLocal,
} from "cortex/ui/src/ui/table/formats";
import { User } from "cortex/ui/src/user/model";
import { Panel } from "cortex/ui/src/view/layout";
import { Incident } from "cortex/ui/src/zeus/view/incidents/model";

import IncidentComments from "./IncidentComments";
import IncidentPriority from "./IncidentPriority";
import IncidentStatus, {
  INCIDENT_STATUS_OPTIONS,
  getIncidentStatusChangeComment,
} from "./IncidentStatus";

const column = createColumnHelper<Event>();
const CONTRIBUTING_EVENTS_LIMIT_MINUTES = 60;
const CONTRIBUTING_EVENTS_RETENTION_MONTHS = 6;

const PRIORITY_LABELS: Record<
  number,
  { color: JollyRancherChipColor; label: string }
> = {
  0: { color: "green", label: "Information" },
  1: { color: "yellow", label: "Warning" },
  2: { color: "red", label: "Critical" },
};

function asArray<T>(value: T | ReadonlyArray<T> | string | null | undefined) {
  if (value == null || value === "") {
    return [] as T[];
  }

  return Array.isArray(value) ? [...value] : [value as T];
}

function formatPriorityLabel(value: number): string {
  switch (value) {
    case 0:
      return "Information";
    case 1:
      return "Warning";
    case 2:
      return "Critical";
    default:
      return `Unknown (${value})`;
  }
}

interface IncidentDetailsDrawerProps {
  alertDetails?: AlertModel | null;
  canComment: boolean;
  canEditStatus?: boolean;
  incident: Incident | null;
  onClose: () => void;
  open: boolean;
  showResolutionCommentByDefault?: boolean;
  users: Record<string, User>;
}

interface ContributingEventsTabProps {
  alertDetails: AlertModel | null;
  applications: Record<string, Application>;
  contributingEvents: ReadonlyArray<Event>;
  contributingEventsError: string;
  contributingEventsLoading: boolean;
  contributingEventsStoredTooLong: boolean;
  incidentWindowEnd: moment.Moment | null;
}

export default function IncidentDetailsDrawer({
  alertDetails = null,
  canComment = true,
  canEditStatus = false,
  incident,
  onClose,
  open,
  showResolutionCommentByDefault = false,
  users,
}: IncidentDetailsDrawerProps) {
  const dispatch = useAppDispatch();
  const applications = useAppSelector(compatApplicationsSelector);
  const device = useAppSelector((state) =>
    incident ? (devicesSelector(state)[incident.device_id] ?? null) : null,
  );
  const [newComment, setNewComment] = useState("");
  const [markResolved, setMarkResolved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<Incident["status"]>(incident?.status);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [resolvedAlertDetails, setResolvedAlertDetails] =
    useState<AlertModel | null>(alertDetails);
  const [contributingEvents, setContributingEvents] = useState<
    ReadonlyArray<Event>
  >([]);
  const [contributingEventsLoading, setContributingEventsLoading] =
    useState(false);
  const [contributingEventsError, setContributingEventsError] = useState("");
  const [contributingEventsStoredTooLong, setContributingEventsStoredTooLong] =
    useState(false);
  const [comments, setComments] = useState<ReadonlyArray<IncidentComment>>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [_, setActiveTab] = useState<"overview" | "details">("overview");

  const trimmedNewComment = newComment.trim();
  const incidentWindowEnd = useMemo(() => {
    if (!incident) {
      return null;
    }

    const createDate = moment.utc(incident.create_date);
    const lastNotified = incident.last_notified
      ? moment.utc(incident.last_notified)
      : null;

    if (lastNotified && lastNotified.isAfter(createDate)) {
      return lastNotified;
    }

    return createDate;
  }, [incident]);

  useEffect(() => {
    if (!open || !incident) {
      return;
    }

    setNewComment("");
    setMarkResolved(showResolutionCommentByDefault && !incident.resolved);
    setSubmitting(false);
    setStatus(incident.status);
    setStatusUpdating(false);
    setResolvedAlertDetails(alertDetails);
    setContributingEvents([]);
    setContributingEventsLoading(false);
    setContributingEventsError("");
    setComments([]);
    setCommentsLoading(true);
    setCommentsError("");
    const retentionCutoff = moment
      .utc()
      .subtract(CONTRIBUTING_EVENTS_RETENTION_MONTHS, "months");
    const contributingEventsStoredTooLong = Boolean(
      incidentWindowEnd?.isBefore(retentionCutoff),
    );
    setContributingEventsStoredTooLong(contributingEventsStoredTooLong);
    setActiveTab("overview");

    let isCancelled = false;

    const loadComments = async () => {
      try {
        const response = await incidentsService.loadComments(incident.id);

        if (!isCancelled) {
          setComments((existingComments) => {
            const loadedComments = response.data.data;
            const loadedIds = new Set(loadedComments.map(({ id }) => id));
            return [
              ...loadedComments,
              ...existingComments.filter(({ id }) => !loadedIds.has(id)),
            ];
          });
        }
      } catch {
        if (!isCancelled) {
          setCommentsError("Failed to load comments.");
        }
      } finally {
        if (!isCancelled) {
          setCommentsLoading(false);
        }
      }
    };

    const loadContributingEvents = async () => {
      try {
        let incidentAlert = alertDetails;

        if (!incidentAlert) {
          const alertResponse = await alertV3Service.load();
          if (isCancelled) {
            return;
          }

          const alerts = alertResponse?.data?.data ?? [];
          incidentAlert =
            alerts.find(
              (candidate: AlertModel) => candidate.id === incident.alert_id,
            ) ?? null;
        }

        if (!incidentAlert) {
          if (contributingEventsStoredTooLong) {
            return;
          }

          setContributingEventsError(
            "Unable to load the alert configuration for this incident.",
          );
          return;
        }

        setResolvedAlertDetails(incidentAlert);

        if (contributingEventsStoredTooLong) {
          return;
        }

        setContributingEventsLoading(true);
        setContributingEventsError("");

        if (!incidentWindowEnd) {
          setContributingEventsError("Unable to determine the incident time.");
          return;
        }

        const queryWindowMinutes = Math.min(
          incidentAlert.time_range ?? 0,
          CONTRIBUTING_EVENTS_LIMIT_MINUTES,
        );
        const startDate = incidentWindowEnd
          .clone()
          .subtract(queryWindowMinutes, "minutes");

        const alertTargetEvents = asArray(incidentAlert.target_events);
        const alertSourceIds = asArray(incidentAlert.source_ids);
        const alertSeverityLevels = asArray(incidentAlert.severity_levels);

        const filters = {
          targets: `d_${incident.device_id}`,
          ...(alertTargetEvents.length ? { name: alertTargetEvents } : {}),
          ...(alertSourceIds.length ? { source: alertSourceIds } : {}),
          ...(alertSeverityLevels.length
            ? { priority: alertSeverityLevels }
            : {}),
        };

        const eventResponse = await eventService.loadV3(
          startDate.format("YYYY-MM-DDTHH:mm:ss"),
          incidentWindowEnd.format("YYYY-MM-DDTHH:mm:ss"),
          filters,
        );

        if (isCancelled) {
          return;
        }

        setContributingEvents(eventResponse?.data?.data ?? []);
      } catch {
        if (!isCancelled && !contributingEventsStoredTooLong) {
          setContributingEventsError("Failed to load contributing events.");
        }
      } finally {
        if (!isCancelled) {
          setContributingEventsLoading(false);
        }
      }
    };

    void loadComments();
    void loadContributingEvents();

    return () => {
      isCancelled = true;
    };
  }, [
    alertDetails,
    incident,
    incidentWindowEnd,
    open,
    showResolutionCommentByDefault,
  ]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }

    setNewComment("");
    setMarkResolved(false);
    onClose();
  }, [onClose, submitting]);

  const handleStatusChange = useCallback(
    async (nextStatus: NonNullable<Incident["status"]>) => {
      if (!incident || statusUpdating || nextStatus === status) {
        return;
      }

      const previousStatus = status;
      setStatus(nextStatus);
      setStatusUpdating(true);

      try {
        await createPromiseApiAction(dispatch, UPDATE_INCIDENT_STATUS, [
          incident.id,
          nextStatus,
        ]);

        try {
          const response = await incidentsService.addComment(
            incident.id,
            getIncidentStatusChangeComment(nextStatus),
          );
          setComments((existingComments) => [
            ...existingComments,
            response.data.data,
          ]);
        } catch {
          showError(
            "Incident status updated, but the timeline entry could not be added.",
          );
        }
      } catch {
        setStatus(previousStatus);
        showError("Failed to update incident status.");
      } finally {
        setStatusUpdating(false);
      }
    },
    [dispatch, incident, status, statusUpdating],
  );

  const handleSubmitComment = useCallback(async () => {
    if (!incident || submitting || !trimmedNewComment) {
      return;
    }

    setSubmitting(true);

    try {
      if (markResolved && !incident.resolved) {
        await createPromiseApiAction(dispatch, RESOLVE_INCIDENT, [
          incident.id,
          {
            comment: trimmedNewComment,
          },
        ]);

        showSuccess("Incident resolved.");
        setNewComment("");
        setMarkResolved(false);
        onClose();
        return;
      }

      const response = await incidentsService.addComment(
        incident.id,
        trimmedNewComment,
      );

      setComments((existingComments) => [
        ...existingComments,
        response.data.data,
      ]);
      setNewComment("");
      showSuccess("Comment added.");
    } catch {
      showError(
        markResolved ? "Failed to resolve incident." : "Failed to add comment.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    dispatch,
    incident,
    markResolved,
    onClose,
    submitting,
    trimmedNewComment,
  ]);

  if (!incident) {
    return null;
  }

  const handleCopyIncidentUrl = async () => {
    try {
      await navigator.clipboard.writeText(
        new URL(`/diagnostics/incidents/${incident.id}`, window.location.origin)
          .href,
      );
      showSuccess("Incident URL copied to clipboard.");
    } catch {
      showError("Failed to copy incident URL.");
    }
  };

  return (
    <Drawer
      dataTestId={{
        drawer: "incident-details-drawer",
      }}
      onClose={handleClose}
      open={open}
      padding={0}
      size="large"
      title={incident.alert_name_snapshot ?? ""}
    >
      <TabsProvider key={`${incident.id}-${open ? "open" : "closed"}`}>
        <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
          {incident.alert_description_snapshot && (
            <Typography
              dataTestId="incident-details-description"
              pt={2}
              px={2}
              variant="body2"
            >
              {incident.alert_description_snapshot}
            </Typography>
          )}
          <Box
            bgcolor={Colors.white}
            flexShrink={0}
            position="sticky"
            pt={1}
            px={2}
            top={0}
            zIndex={1}
          >
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
            >
              <TabRow dataTestId="incident-details-tabs" />
              <Button
                dataTestId="incident-details-share-button"
                icon="share"
                onClick={handleCopyIncidentUrl}
                size="small"
                variant="outlined"
              >
                Share
              </Button>
            </Stack>
          </Box>
          <Box flex={1} minHeight={0} overflow="auto">
            <Tabs>
              <Tab
                defaultSelected
                label="Overview"
                onClick={() => setActiveTab("overview")}
              >
                <Stack>
                  <DrawerSummary
                    canEditStatus={canEditStatus}
                    device={device ?? null}
                    incident={incident}
                    onStatusChange={handleStatusChange}
                    status={status}
                    statusUpdating={statusUpdating}
                  />
                  <IncidentComments
                    canComment={canComment}
                    canResolve={!incident.resolved}
                    comments={comments}
                    error={commentsError}
                    loading={commentsLoading}
                    markResolved={markResolved}
                    newComment={newComment}
                    onCommentChange={setNewComment}
                    onMarkResolvedChange={setMarkResolved}
                    onSubmit={() => void handleSubmitComment()}
                    resolution={
                      incident.resolved
                        ? {
                            comment: incident.comment,
                            date: incident.resolve_date,
                            userId: incident.user_id,
                          }
                        : undefined
                    }
                    submitting={submitting}
                    users={users}
                  />
                </Stack>
              </Tab>
              <Tab label="Details" onClick={() => setActiveTab("details")}>
                <ContributingEventsTab
                  alertDetails={resolvedAlertDetails}
                  applications={applications}
                  contributingEvents={contributingEvents}
                  contributingEventsError={contributingEventsError}
                  contributingEventsLoading={contributingEventsLoading}
                  contributingEventsStoredTooLong={
                    contributingEventsStoredTooLong
                  }
                  incidentWindowEnd={incidentWindowEnd}
                />
              </Tab>
            </Tabs>
          </Box>
        </Box>
      </TabsProvider>
    </Drawer>
  );
}

function ContributingEventsTab({
  alertDetails,
  applications,
  contributingEvents,
  contributingEventsError,
  contributingEventsLoading,
  contributingEventsStoredTooLong,
  incidentWindowEnd,
}: ContributingEventsTabProps) {
  const contributingEventColumns = useMemo(
    () => [
      column.accessor("severity", {
        header: "Priority",
        cell: (prop) => {
          const value = prop.getValue();
          const priority = PRIORITY_LABELS[value] ?? {
            color: "gray",
            label: `Unknown (${value})`,
          };

          return (
            <JollyRancherChip color={priority.color} label={priority.label} />
          );
        },
        enableSorting: false,
        meta: {
          width: 150,
        },
      }),
      column.accessor("name", {
        header: "Event",
        cell: (prop) => prop.getValue(),
        enableSorting: false,
        meta: {
          width: 200,
        },
      }),
      column.accessor("message", {
        header: "Message",
        cell: (prop) => prop.getValue(),
        enableSorting: false,
      }),
      column.accessor("event_date", {
        header: "Date",
        cell: (prop) => formatDateTimeLocal(prop.getValue()),
        enableSorting: false,
        meta: {
          width: 200,
        },
      }),
      column.accessor("source", {
        header: "Source",
        cell: (prop) => formatApplicationSource(applications, prop.getValue()),
        enableSorting: false,
        meta: {
          width: 180,
        },
      }),
    ],
    [applications],
  );

  const sortedEvents = useMemo(() => {
    return [...contributingEvents].sort((left, right) => {
      return (
        moment.utc(right.event_date).valueOf() -
        moment.utc(left.event_date).valueOf()
      );
    });
  }, [contributingEvents]);

  return (
    <Stack>
      {contributingEventsStoredTooLong ? (
        <Alert severity="info">
          Contributing events are only stored for 6 months
        </Alert>
      ) : (
        <>
          {contributingEventsLoading && (
            <Alert severity="info">Loading contributing events...</Alert>
          )}
          {contributingEventsError && (
            <Alert severity="error">{contributingEventsError}</Alert>
          )}
          {alertDetails && (
            <Box bgcolor={Colors.gray200} p={2}>
              <Panel mb={0} p={0}>
                <DescriptionList breakpoint="md">
                  <DescriptionListItem label="Event Names">
                    {asArray(alertDetails.target_events).length
                      ? asArray(alertDetails.target_events).join(", ")
                      : "Any"}
                  </DescriptionListItem>
                  <DescriptionListItem label="Sources">
                    {asArray(alertDetails.source_ids).length
                      ? asArray(alertDetails.source_ids)
                          .map((sourceId) =>
                            formatApplicationSource(
                              applications,
                              sourceId as number,
                            ),
                          )
                          .join(", ")
                      : "Any"}
                  </DescriptionListItem>
                  <DescriptionListItem label="Priorities">
                    {asArray(alertDetails.severity_levels).length
                      ? asArray(alertDetails.severity_levels)
                          .map((severityLevel) =>
                            formatPriorityLabel(severityLevel as number),
                          )
                          .join(", ")
                      : "Any"}
                  </DescriptionListItem>
                  <DescriptionListItem label="Event Window">
                    {formatDateTimeLocal(
                      incidentWindowEnd
                        ?.clone()
                        .subtract(
                          Math.min(
                            alertDetails.time_range ?? 0,
                            CONTRIBUTING_EVENTS_LIMIT_MINUTES,
                          ),
                          "minutes",
                        ) ?? "",
                    )}{" "}
                    - {formatDateTimeLocal(incidentWindowEnd ?? "")}
                  </DescriptionListItem>
                </DescriptionList>
              </Panel>
            </Box>
          )}
          {!contributingEventsLoading && !contributingEventsError && (
            <DataTable
              columns={contributingEventColumns}
              dataTestId={{
                root: "incident-details-contributing-events",
              }}
              emptyState={
                <Box p={2} textAlign="center">
                  <Typography color={Colors.gray1000} variant="body2">
                    No events were found
                  </Typography>
                </Box>
              }
              initialPageSize={10}
              rows={sortedEvents}
            />
          )}
        </>
      )}
    </Stack>
  );
}

interface DrawerSummaryProps {
  canEditStatus: boolean;
  device: Device | null;
  incident: Incident;
  onStatusChange: (status: NonNullable<Incident["status"]>) => void;
  status: Incident["status"];
  statusUpdating: boolean;
}

function DrawerSummary({
  canEditStatus,
  device,
  incident,
  onStatusChange,
  status,
  statusUpdating,
}: DrawerSummaryProps) {
  const networkContent =
    device?.network_id != null && device.network_name ? (
      <Link to={`/networks/${device.network_id}`}>{device.network_name}</Link>
    ) : (
      "--"
    );

  return (
    <Box bgcolor={Colors.gray200} p={2}>
      <Panel mb={0} p={0}>
        <DescriptionList breakpoint="md">
          <DescriptionListItem
            dataTestId={{
              component: "incident-details-id",
            }}
            label="ID"
          >
            {incident.id}
          </DescriptionListItem>
          <DescriptionListItem
            dataTestId={{
              component: "incident-details-priority",
            }}
            label="Priority"
          >
            <IncidentPriority priority={incident.alert_priority_snapshot} />
          </DescriptionListItem>
          {!incident.resolved && (
            <DescriptionListItem
              dataTestId={{
                component: "incident-details-status",
              }}
              label="Status"
            >
              {canEditStatus ? (
                <AutocompleteSelectInput
                  dataTestId="incident-status-select"
                  disableClearable
                  disabled={statusUpdating}
                  inputProps={{
                    sx: { minWidth: 200 },
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={(_event, option) => onStatusChange(option.value)}
                  options={INCIDENT_STATUS_OPTIONS}
                  size="small"
                  value={INCIDENT_STATUS_OPTIONS.find(
                    ({ value }) => value === status,
                  )}
                />
              ) : (
                <IncidentStatus status={status} />
              )}
            </DescriptionListItem>
          )}
          <DescriptionListItem
            dataTestId={{
              component: "incident-details-device",
            }}
            label="Device"
          >
            <Link to={`/devices/${incident.device_device_id}`}>
              {incident.device_name}
            </Link>
          </DescriptionListItem>
          <DescriptionListItem
            dataTestId={{
              component: "incident-details-network",
            }}
            label="Network"
          >
            {networkContent}
          </DescriptionListItem>
          <DescriptionListItem
            dataTestId={{
              component: "incident-details-create_date",
            }}
            label="First Occurrence Date"
          >
            {formatDateTimeLocal(incident.create_date)}
          </DescriptionListItem>
          <DescriptionListItem
            dataTestId={{
              component: "incident-details-last_notified",
            }}
            label="Last Notified Date"
          >
            {formatDateTimeLocal(incident.last_notified ?? "")}
          </DescriptionListItem>
        </DescriptionList>
      </Panel>
    </Box>
  );
}
